import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { useContactModal } from "@/components/ContactModal";
import { useAuth } from "@/context/AuthContext";

const brandCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];

const NAV_ITEMS: { label: string; to?: string; action?: "contact" }[] = [
  { label: "Home", to: "/" },
  { label: "Experience Fanpictor", to: "/demo" },
  { label: "Understand Fanpictor", to: "/content-hub" },
  { label: "Get in Touch", action: "contact" },
];

const MenuBurger = () => {
  const [open, setOpen] = useState(false);
  const { open: openContact } = useContactModal();
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const itemClass =
    "font-display text-xl font-bold uppercase tracking-tight text-foreground transition-colors hover:text-muted-foreground md:text-4xl";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="flex h-10 w-10 items-center justify-center text-foreground transition-colors hover:text-muted-foreground"
      >
        <Menu className="h-6 w-6" />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-[9999] bg-background/80 backdrop-blur-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: brandCurve }}
              onClick={() => setOpen(false)}
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center text-foreground transition-colors hover:text-muted-foreground md:right-10 md:top-8"
              >
                <X className="h-6 w-6" />
              </button>

              <nav
                className="flex h-full flex-col items-center justify-center gap-6 md:gap-10"
                onClick={(e) => e.stopPropagation()}
              >
                {NAV_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.05 + i * 0.05, ease: brandCurve }}
                  >
                    {item.action === "contact" ? (
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          openContact();
                        }}
                        className={itemClass}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link to={item.to!} onClick={() => setOpen(false)} className={itemClass}>
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
                {session && (
                  <motion.div
                    key="logout"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.05 + NAV_ITEMS.length * 0.05, ease: brandCurve }}
                  >
                    <button
                      type="button"
                      onClick={async () => {
                        setOpen(false);
                        await signOut();
                        navigate("/login", { replace: true });
                      }}
                      className={itemClass}
                    >
                      Log out
                    </button>
                  </motion.div>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default MenuBurger;
